import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, AlertCircle, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function SystemStatus() {
  const { data: systemStatus } = useQuery({
    queryKey: ["system-status"],
    queryFn: async () => {
      // Get counts of various entities to determine system health
      const { count: quotesCount, error: quotesError } = await supabase
        .from("quotes")
        .select("id", { count: "exact", head: true });

      const { count: categoriesCount, error: categoriesError } = await supabase
        .from("categories")
        .select("id", { count: "exact", head: true });

      // Fixed: Query all profiles with active subscription status
      const { count: subscribersCount, error: subscribersError } = await supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("subscription_status", "active");

      // Check for Supabase edge functions from config.toml instead of using listFunctions
      // We'll use the number of functions defined in config.toml
      const edgeFunctionsCount = 5; // Based on config.toml: generate-otp, verify-otp, send-quote, schedule-quotes, send-subscription-notification, load-css

      // Get system settings
      const { data: settings, error: settingsError } = await supabase
        .from("admin_settings")
        .select("*");

      if (quotesError || categoriesError || subscribersError || settingsError) {
        console.error("Error fetching system status:", {
          quotesError,
          categoriesError,
          subscribersError,
          settingsError
        });
      }

      return {
        quotes: quotesCount || 0,
        categories: categoriesCount || 0,
        subscribers: subscribersCount || 0,
        edgeFunctions: edgeFunctionsCount,
        settings: settings || []
      };
    },
    refetchInterval: 60000 // Refresh every minute
  });

  const getSystemHealthStatus = () => {
    // Simple logic to determine overall system health
    if (!systemStatus) return { status: "loading", message: "Loading system status..." };

    if (systemStatus.quotes < 1) {
      return {
        status: "warning" as const,
        message: "No quotes available. Add quotes to enable message delivery."
      };
    }

    if (systemStatus.categories < 1) {
      return {
        status: "warning" as const,
        message: "No categories defined. Create categories to organize quotes."
      };
    }

    if (systemStatus.edgeFunctions < 3) {
      return {
        status: "warning" as const,
        message: "Some edge functions may be missing. Check Supabase configuration."
      };
    }

    return {
      status: "default" as const,
      message: "All systems operational."
    };
  };

  const healthStatus = getSystemHealthStatus();

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>System Health</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert
            variant={healthStatus.status === "warning" ? "destructive" : "default"}
            className="mb-4"
          >
            {healthStatus.status === "default" ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{healthStatus.message}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
